// Hierarchical (ELK) renderer — FXC-8538 (Warp).
//
// A second concrete `GraphRenderer`: an SVG layered DAG, ELK-routed, so
// lineage reads as proper left-to-right layers (geometry → mesh → case →
// scalars) instead of a force blob. Deterministic + dependency-light to
// draw (SVG); ELK runs async off the render path. Registered as
// "hierarchical"; pick the best renderer per surface.

import { useEffect, useId, useMemo, useRef, useState } from "react";

import { colourFor, edgeColourFor } from "../core/palette";
import type { GraphNode } from "../core/types";
import type { GraphControls, GraphRendererProps } from "./GraphRenderer";
import { layoutWithElk, type ElkLayout } from "./elkLayout";

const PAD = 24;

export function HierarchicalRenderer({
  nodes,
  edges,
  width,
  height,
  highlightIds = null,
  colourForType = colourFor,
  colourForPredicate = edgeColourFor,
  nodeColor,
  theme = "dark",
  layoutDirection = "RIGHT",
  onNodeClick,
  onNodeRightClick,
  onNodeHover,
  onReady,
}: GraphRendererProps) {
  const [layout, setLayout] = useState<ElkLayout | null>(null);
  const [layoutError, setLayoutError] = useState<string | null>(null);
  const arrowMarkerId = `tg-arrow-${useId().replace(/:/g, "")}`;
  const byId = useMemo(() => {
    const m = new Map<string, GraphNode>();
    for (const n of nodes) m.set(n.id, n);
    return m;
  }, [nodes]);

  // Compute (async) ELK layout whenever the data changes.
  useEffect(() => {
    let cancelled = false;
    setLayout(null);
    setLayoutError(null);
    layoutWithElk(nodes, edges, { direction: layoutDirection })
      .then((l) => {
        if (!cancelled) setLayout(l);
      })
      .catch((error: unknown) => {
        if (!cancelled) setLayoutError(errorMessage(error));
      });
    return () => {
      cancelled = true;
    };
  }, [nodes, edges, layoutDirection]);

  // SVG auto-fits, so zoom-to-fit is a no-op — still satisfy the contract.
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  useEffect(() => {
    const controls: GraphControls = { zoomToFit: () => undefined };
    onReadyRef.current?.(controls);
    return () => onReadyRef.current?.(null);
  }, []);

  if (layoutError) {
    return (
      <div style={{ width, height, display: "grid", placeItems: "center", font: "12px ui-monospace, monospace", opacity: 0.72 }}>
        graph layout failed: {layoutError}
      </div>
    );
  }

  if (!layout) {
    return (
      <div style={{ width, height, display: "grid", placeItems: "center", font: "12px ui-monospace, monospace", opacity: 0.6 }}>
        laying out…
      </div>
    );
  }

  const vb = `${-PAD} ${-PAD} ${Math.max(1, layout.width) + PAD * 2} ${Math.max(1, layout.height) + PAD * 2}`;

  return (
    <svg width={width} height={height} viewBox={vb} preserveAspectRatio="xMidYMid meet"
      style={{ display: "block", maxWidth: "100%", background: "transparent" }}>
      <defs>
        <marker id={arrowMarkerId} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M0,0 L8,4 L0,8 z" fill="rgba(148,163,184,0.7)" />
        </marker>
      </defs>

      {layout.edges.map((e, i) => {
        if (e.points.length < 2) return null;
        const hot = highlightIds?.has(e.source) || highlightIds?.has(e.target);
        const d = e.points.map((p, j) => `${j === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
        return (
          <path key={i} d={d} fill="none"
            stroke={colourForPredicate(e.predicate)}
            strokeWidth={hot ? 2 : 1}
            opacity={hot ? 1 : 0.6}
            markerEnd={`url(#${arrowMarkerId})`} />
        );
      })}

      {layout.nodes.map((pn) => {
        const node = byId.get(pn.id);
        if (!node) return null;
        const fill = nodeColor?.(node) ?? colourForType(node.type);
        const hot = highlightIds?.has(pn.id) ?? false;
        return (
          <g key={pn.id} transform={`translate(${pn.x},${pn.y})`} style={{ cursor: "pointer" }}
            onClick={() => onNodeClick?.(node)}
            onContextMenu={(ev) => { ev.preventDefault(); onNodeRightClick?.(node); }}
            onMouseEnter={() => onNodeHover?.(node)}
            onMouseLeave={() => onNodeHover?.(null)}>
            <rect width={pn.width} height={pn.height} rx={6}
              fill={fill} fillOpacity={hot ? 0.32 : 0.16}
              stroke={fill} strokeWidth={hot ? 2 : 1} />
            <text x={pn.width / 2} y={pn.height / 2 + 3.5} textAnchor="middle"
              fontSize={11} fontFamily="ui-sans-serif, system-ui, sans-serif"
              fill={theme === "light" ? "rgba(20,22,28,0.95)" : "rgba(255,255,255,0.92)"} style={{ pointerEvents: "none" }}>
              {clip(node.label)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function clip(label: string): string {
  return label.length > 28 ? `${label.slice(0, 27)}…` : label;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
